import { useMemo, useRef, useState } from "react";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  useAllUserRegister,
  usePointLogsRegister,
} from "src/common/hooks/useUserRegister";
import { formatBirthday } from "src/common/hooks/useFormatBirthDay";
import { Buttons } from "src/components/atoms/Buttons";
import { ReactComponent as IconMenu } from "../../styles/assets/svg/icon_menu.svg";
import { MenuList } from "src/components/molecules/MenuList";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { confirmDialog } from "src/components/containers/ConfirmDialog";
import { adminLogout } from "src/common/functions/authFunctions";

const menuListDummy = {
  category: "회원 관리",
  menu: [
    { menu_name: "회원 정보 관리", path: "/admin/users" },
    { menu_name: "회원 로그 이력", path: "/admin/logs" },
  ],
};

const Title = styled.h4`
  padding-left: 10px;
  margin-bottom: 12px;
  font-size: 1.8rem;
`;

const GridTotal = styled.div`
  display: flex;
  color: ${(props) => props.theme.colors.default};
  font-size: 1.2rem;
  font-weight: 700;
  align-items: center;
  margin-bottom: 8px;
`;
export const AdminPage = () => {
  const gridRef = useRef<any>(null);
  const [sideOpen, setSideOpen] = useState<boolean>(true);
  const { pathname } = useLocation();

  // 유저 정보 불러오기
  const { data: userInfo = [] } = useAllUserRegister();

  const { data: pointLogs = [] } = usePointLogsRegister();

  const enrichedPointLogs = useMemo(() => {
    const userInfoMap = new Map(userInfo.map((user) => [user.oid, user]));
    return pointLogs.map((log: any) => {
      const user = userInfoMap.get(log.userOid);
      return {
        ...log,
        userName: user?.userName || "",
        userId: user?.userId || "",
        gender: user?.gender || "",
      };
    });
  }, [pointLogs, userInfo]);

  const userColumnDefs: ColDef[] = [
    {
      headerName: "No",
      valueGetter: "node.rowIndex + 1",
      width: 50,
    },
    { headerName: "이름", field: "userName", width: 80 },
    { headerName: "아이디", field: "userId", width: 120 },
    {
      headerName: "포인트",
      field: "point",
      width: 80,
    },
    {
      headerName: "성별",
      field: "gender",
      width: 60,
      cellRenderer: (params: any) =>
        params.data.gender === "M" ? "남성" : "여성",
    },
    {
      headerName: "생년월일",
      field: "birthyear",
      cellRenderer: (params: any) =>
        params.data.birthyear !== null
          ? params.data.birthyear + "." + formatBirthday(params.data.birthday)
          : "미입력",
      width: 110,
    },
    {
      width: 250,
      headerName: "이메일",
      field: "email",
    },
    {
      headerName: "키",
      field: "height",
      width: 80,
      cellRenderer: (params: any) =>
        params.data.height ? `${params.data.height}cm` : "미입력",
    },
    {
      headerName: "몸무게",
      field: "weight",
      width: 80,
      cellRenderer: (params: any) =>
        params.data.weight ? `${params.data.weight}kg` : "미입력",
    },
    {
      headerName: "운동 횟수",
      field: "workoutCountName",
      width: 160,
      cellRenderer: (params: any) =>
        params.data.workoutCount ? params.data.workoutCountName : "미입력",
    },
    {
      headerName: "사용 여부",
      field: "useYn",
      width: 120,
      cellRenderer: (params: any) => (params.data.useYn ? "사용중" : "미사용"),
    },
    {
      headerName: "가입일시",
      field: "createdAt",
      flex: 1,
      cellRenderer: (params: any) =>
        new Date(params.data.createdAt).toLocaleString(),
    },
  ];

  const logsColumnDefs: ColDef[] = [
    {
      headerName: "No",
      valueGetter: "node.rowIndex + 1",
      width: 80,
    },
    { headerName: "유저oid", field: "userOid", width: 500 },
    { headerName: "이름", field: "userName", width: 80 },
    { headerName: "아이디", field: "userId", width: 120 },
    {
      headerName: "성별",
      field: "gender",
      width: 60,
      cellRenderer: (params: any) =>
        params.data.gender === "M" ? "남성" : "여성",
    },
    { headerName: "내역", field: "description", width: 150 },
    { headerName: "적립내역", field: "pointChange", width: 150 },
    {
      headerName: "등록날짜",
      field: "createdAt",
      flex: 1,
    },
  ];

  const currentMenuTitle =
    menuListDummy.menu.find((m) => m.path === pathname)?.menu_name || "";

  const currentRowData = (() => {
    switch (currentMenuTitle) {
      case "회원 정보 관리":
        return userInfo;

      case "회원 로그 이력":
        return enrichedPointLogs;
      default:
        return [];
    }
  })();

  const dataLength = currentRowData && currentRowData.length;

  const currentColumnDefs = (() => {
    switch (currentMenuTitle) {
      case "회원 정보 관리":
        return userColumnDefs;

      case "회원 로그 이력":
        return logsColumnDefs;

      default:
        return userColumnDefs; // fallback
    }
  })();

  const handleLogOut = async () => {
    const isConfirm = await confirmDialog({
      text: "로그아웃 하시겠습니까?",
      title: "로그아웃",
      options: {
        trueButtonText: "돌아가기",
        falseButtonText: "로그아웃",
      },
    });
    if (!isConfirm) {
      adminLogout();
    }
  };

  return (
    <>
      <div
        style={{
          padding: "10px 20px",
          color: "#222",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Buttons
            type="button"
            size="md"
            layout="icon"
            icon={<IconMenu width={14} height={14} />}
            onClick={() => setSideOpen(!sideOpen)}
          />
          <div style={{ fontSize: "20px", fontWeight: 600 }}>관리자 페이지</div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <div style={{ width: 18, height: 18, background: "#fff" }}>
            <svg width={18} height={18} viewBox="0 0 24 24">
              <path
                d="M9 18l6-6-6-6"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </div>
          <button
            onClick={handleLogOut}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fff",
              border: "none",
              cursor: "pointer",
              opacity: 1,
              textAlign: "left",
            }}
          >
            <span
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: "var(--neutral-400, #A3A3A3)",
              }}
            >
              로그아웃
            </span>
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: "auto auto 60px auto auto",
          gridTemplateColumns: sideOpen ? "230px auto" : "0px auto",
          transition: "grid-template-columns 0.2s ease",
        }}
      >
        <div
          style={{
            width: "230px",
            padding: "20px 10px",
            overflow: "hidden",
            zIndex: 1,
          }}
        >
          <MenuList data={menuListDummy} />
        </div>
        <div
          className="ag-theme-quartz"
          style={{
            height: "calc(100vh - 70px)",
            minHeight: "300px",
            padding: 20,
            background: "#f7f7f7",
            borderRadius: "5px",
            zIndex: 2,
          }}
        >
          <Title>{currentMenuTitle}</Title>
          <GridTotal>총 {userInfo.length > 0 ? dataLength : 0}건</GridTotal>
          <div style={{ width: "100%", height: "calc(100% - 55px)" }}>
            <AgGridReact
              theme="legacy"
              ref={gridRef}
              rowData={currentRowData}
              columnDefs={currentColumnDefs}
              headerHeight={30}
              rowHeight={44}
              overlayNoRowsTemplate="조회 결과가 없습니다"
              defaultColDef={{
                resizable: true,
                sortable: true,
                // filter: true,
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};
